import React, { useEffect, useState } from 'react';
import { I18n } from 'react-redux-i18n';
import get from 'lodash/get';
import map from 'lodash/map';
import join from 'lodash/join';

import Card from 'components/Card';
import CardHeader from 'components/CardHeader';
import BarGraph from 'components/Charts/BarGraph';
import Cell from 'components/Cell';
import ExportButton from 'components/Export';
import Spinner from 'components/Spinner';
import Header from 'components/Table/Header';
import Table from 'components/Table';
import ChartCompareBloc from '../../components/ChartCompareBloc';
import ChartInputsBloc from '../../components/ChartInputsBloc';
import {
  ContainerPills,
  HeaderBar,
  HeaderRightComponents,
  VintageTooltip
} from './container';
import { getRivals } from './helpers';

import { useDistributionVintage } from 'apiTyped/hooks/distribution.query';

const Vintages = ({ form, client_marques, isBranded }) => {
  const [state, setState] = useState({
    wine: null,
    vintage: null,
    vintageStart: null,
    vintageEnd: null,
    competitorName: null,
    activeRadio: 'none',
    data: [],
    sorted: [],
    expanded: {},
    campaign: null
  });

  // Use the new hook to fetch vintage data
  const { isLoading, data } = useDistributionVintage(form?.currentCampaign?.id);

  const {
    wine,
    vintageStart,
    vintageEnd,
    competitorName,
    activeRadio,
    sorted,
    expanded
  } = state;

  // Set first wine when data becomes available
  useEffect(() => {
    if (data?.client_wines?.length > 0 && !wine) {
      setFirstWineInState();
    }
  }, [data?.client_wines?.length, wine]);

  // Campaign handling is now managed by parent Distribution component

  const getWineOptions = () => {
    const { client_wines = [] } = data || {};
    const { rival_wines = [] } = data || {};
    if (isBranded) {
      const options = [...new Set(getWineOptionsFromBrands())];
      return [
        ...new Set(
          client_wines
            .filter((item) => {
              if (!options.includes(item.wine)) {
                return 0;
              }
              if (!item.dn) {
                const rivals = getRivals(client_wines, rival_wines, item.wine);
                if (!rivals || rivals.length < 1) {
                  return 0;
                }
              }
              return 1;
            })
            .map((item) => item.wine)
        )
      ]
        .sort()
        .map((item) => ({ label: item, value: item }));
    }
    return [
      ...new Set(
        client_wines
          .filter((item) => {
            if (!item.dn) {
              const rivals = getRivals(client_wines, rival_wines, item.wine);
              if (!rivals || rivals.length < 1) {
                return 0;
              }
            }
            return 1;
          })
          .map((item) => item.wine)
      )
    ]
      .sort()
      .map((item) => ({ label: item, value: item }));
  };

  const setFirstWineInState = () => {
    if (isBranded) {
      const wineOptions = getWineOptionsFromBrands();
      const wineData = [...(data?.client_wines || [])].filter(
        (item) => wineOptions.includes(item.wine) && item.dn
      );
      setState((prev) => ({
        ...prev,
        wine: {
          label: get(wineData, '[0].wine'),
          value: get(wineData, '[0].wine')
        }
      }));
    } else {
      if (form?.wine && form.wine.length > 0) {
        setState((prev) => ({
          ...prev,
          wine: {
            label: get(form, 'wine[0].label'),
            value: get(form, 'wine[0].label')
          }
        }));
      } else {
        setState((prev) => ({
          ...prev,
          wine: {
            label: get(data, 'client_wines[0].wine'),
            value: get(data, 'client_wines[0].wine')
          }
        }));
      }
    }
  };

  const handleSort = (id) => {
    let sortedState = [...sorted];
    let desc = true;
    if (sortedState.length > 0 && sortedState[0].id === id) {
      desc = !sortedState[0].desc;
    }
    sortedState = [{ id, desc }];
    setState((prev) => ({ ...prev, sorted: sortedState, expanded: {} }));
  };

  const handleChangeChart = (field, value) => {
    if (field === 'wine') {
      return setState((prev) => ({
        ...prev,
        wine: value,
        activeRadio: 'none',
        competitorName: null
      }));
    }
    if (field === 'competitorName') {
      return setState((prev) => ({
        ...prev,
        activeRadio: 'rival',
        competitorName: value
      }));
    }
    setState((prev) => ({ ...prev, [field]: value }));
  };

  const getWineOptionsFromBrands = () => {
    const { wine } = form || {};

    if (!client_marques || !wine) {
      return [];
    }

    const winelabels = wine.map((item) => item.label);
    const wineOptions = [];
    client_marques.forEach((marque) => {
      if (winelabels.includes(marque.marque_name)) {
        wineOptions.push(...marque.wines);
      }
    });
    return wineOptions;
  };

  const filterByGlobalFilters = () => {
    const { wine = [], competitors = [] } = form || {};
    const statsData = data?.stats || [];
    const wineNames = isBranded
      ? getWineOptionsFromBrands()
      : wine.map((item) => item.label);
    return statsData.filter(
      (item) =>
        wineNames.includes(item.wine_name) ||
        competitors.includes(item.wine_name)
    );
  };

  const formatDataTable = () => {
    const statsData = data?.stats || [];
    let formattedData = [...statsData];
    formattedData = filterByGlobalFilters();
    formattedData = formattedData.filter(
      (item) =>
        item.tx_conso ||
        item.tx_new ||
        item.avg_vintage_ty ||
        item.avg_vintage_ly
    );
    return formattedData.map((item) => {
      const tc =
        item.tx_conso || item.tx_conso === 0
          ? (item.tx_conso * 100).toLocaleString('fr-FR', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }) + ' %'
          : '-';
      const tn =
        item.tx_new || item.tx_new === 0
          ? (item.tx_new * 100).toLocaleString('fr-FR', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }) + ' %'
          : '-';
      const bestVintage = item.most_consumed_vintage;
      const topRef = item.top_three_new_vintages;

      return {
        ...item,
        wine: item.wine_name,
        topRef,
        avg_price: item.avg_vintage_ty ? item.avg_vintage_ty.toFixed(1) : '-',
        bestVintage,
        tn,
        tc,
        avgPriceLastYear:
          item.avg_vintage_ly || item.avg_vintage_ly === 0
            ? item.avg_vintage_ly.toFixed(1)
            : '-'
      };
    });
  };

  const filterDataSet = (dataSet, wine) => {
    return [...dataSet].filter(
      (item) =>
        item.avg_price &&
        !isNaN(parseInt(item.vintage, 10)) &&
        (!vintageStart || item.vintage >= vintageStart.value) &&
        (!vintageEnd || item.vintage <= vintageEnd.value) &&
        wine &&
        item.wine === wine.label &&
        (!item.client_wine || item.client_wine === get(wine, 'label'))
    );
  };

  const getDataChart = () => {
    const client_wines = data?.client_wines || [];
    const rival_wines = data?.rival_wines || [];
    let dataChart = filterDataSet(client_wines, wine);

    if (activeRadio === 'year') {
      const client_wines_ly = data?.client_wines_ly || [];
      const data_ly = filterDataSet(client_wines_ly, wine);
      dataChart = [dataChart, data_ly];
    } else if (competitorName && activeRadio === 'rival') {
      const competitorData = filterDataSet(rival_wines, competitorName);
      dataChart = [dataChart, competitorData];
    } else {
      dataChart = [dataChart];
    }

    dataChart = dataChart.map((set) =>
      set
        .map((item) => ({
          ...item,
          name: item.vintage,
          value: item.dn ? item.dn : 0
        }))
        .sort(function (a, b) {
          if (a && b) {
            return Number(a.vintage) - Number(b.vintage);
          }
          return 0;
        })
    );
    let setNames = [];
    let wines = [];
    dataChart.forEach((set) => {
      if (set && set.length > 0) {
        setNames.push(...set.map((item) => item.name));
        wines.push(get(set, '[0].wine'));
      }
    });
    setNames = [...new Set(setNames)].sort((a, b) => Number(a) - Number(b));
    dataChart.forEach((set, setIndex) => {
      setNames.forEach((name, index) => {
        if (!set[index] || set[index].name !== name) {
          set.splice(index, 0, {
            name,
            value: 0,
            wine: wines[setIndex]
          });
        }
      });
    });
    return dataChart;
  };

  const getCompetitorNames = (dataSet) => {
    return [...new Set(dataSet.map((item) => get(item, 'wine')))];
  };

  const getColumns = () => {
    return [
      {
        accessor: 'wine',
        Header: () => (
          <Header
            sortable={true}
            sort={() => handleSort('wine')}
            styles={`justify-content:flex-start;padding-left:30px`}
            title={I18n.t('restaurants&caves.vintage.columns.wine')}
          />
        ),
        Cell: (props) => (
          <Cell
            {...props}
            font="sfcompact_text"
            styles={`justify-content:flex-start;padding-left:30px;font-weight:600;`}
          >
            {props.value}
          </Cell>
        ),
        minWidth: 150
      },
      {
        Header: () => (
          <Header
            sortable={true}
            sort={() => handleSort('avg_price')}
            title={I18n.t('restaurants&caves.vintage.columns.lastReport')}
          />
        ),
        sortMethod: (a, b) => Number(a) - Number(b),
        Cell: (props) => <Cell {...props}>{props.value}</Cell>,

        accessor: 'avg_price'
      },
      {
        Header: () => (
          <Header
            sortable={true}
            sort={() => handleSort('avgPriceLastYear')}
            title={I18n.t('restaurants&caves.vintage.columns.previousReport')}
          />
        ),
        Cell: (props) => <Cell {...props} />,
        sortMethod: (a, b) => Number(a) - Number(b),
        accessor: 'avgPriceLastYear'
      },
      {
        Header: () => (
          <Header
            sortable={true}
            sort={() => handleSort('tc')}
            title={I18n.t('restaurants&caves.vintage.columns.tc')}
          />
        ),
        Cell: (props) => <Cell value={props.value} {...props} />,
        sortMethod: (a, b) =>
          Number(a.substring(0, a.length - 1)) -
          Number(b.substring(0, b.length - 1)),
        accessor: 'tc'
      },
      {
        Header: () => (
          <Header
            sortable={true}
            sort={() => handleSort('tn')}
            title={I18n.t('restaurants&caves.vintage.columns.tn')}
          />
        ),

        sortMethod: (a, b) =>
          Number(a.substring(0, a.length - 1)) -
          Number(b.substring(0, b.length - 1)),
        Cell: (props) => <Cell {...props}>{props.value}</Cell>,

        accessor: 'tn'
      },
      {
        Header: () => (
          <Header title={I18n.t('restaurants&caves.vintage.columns.topRef')} />
        ),
        Cell: (props) => <Cell {...props}>{props.value}</Cell>,

        accessor: 'topRef'
      },
      {
        Header: () => (
          <Header
            sortable={true}
            sort={() => handleSort('bestVintage')}
            title={I18n.t('restaurants&caves.vintage.columns.bestVintage')}
          />
        ),
        Cell: (props) => <Cell {...props}>{props.value}</Cell>,

        accessor: 'bestVintage'
      }
    ];
  };

  const ordenedData = (formattedData, wineParam) => {
    let ordenedDataResult = [];

    formattedData.map((item) => {
      if (get(item, 'wine_name') === get(wineParam, 'value')) {
        ordenedDataResult.unshift(item);
      } else {
        ordenedDataResult.push(item);
      }
      return item;
    });

    return ordenedDataResult;
  };

  const renderTooltip = (dataSet, labels, totalDn) => {
    let dataTooltip = [...dataSet];
    return (
      <VintageTooltip>
        {dataTooltip
          .filter((set) => set.value)
          .map((set, index) => (
            <div key={index} className="rowVintageTooltip">
              <p>{`${set.wine} ${
                index === 1 && set.wine === dataTooltip[0].wine ? 'N-1 ' : ''
              } ${set.vintage ? set.vintage : ''}`}</p>
              <div>
                <p>{`${set.value} ${
                  totalDn[0] &&
                  set.value &&
                  '( ' +
                    ((set.value / totalDn[0]) * 100).toLocaleString('fr-FR', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2
                    }) +
                    '% )'
                }`}</p>
              </div>
            </div>
          ))}
      </VintageTooltip>
    );
  };

  const getTotalDn = () => {
    return [get(form, 'currentCampaign.dn', 0)];
  };

  const getDataTable = () => {
    const formattedData = formatDataTable();
    return ordenedData(formattedData, wine);
  };

  const getVintageOptions = () => {
    const clientWines = data?.client_wines || [];
    return [
      ...new Set(
        clientWines
          .filter((item) => !isNaN(item.vintage))
          .map((item) => item.vintage)
      )
    ]
      .sort()
      .reverse()
      .map((item) => ({ label: item, value: item }));
  };

  const getLabels = (dataSet) => {
    let labels = [];
    if (dataSet && dataSet.length > 0) {
      labels = [
        ...new Set(
          dataSet.map((set) =>
            set && set.length > 0 ? get(set, '[0].wine') : ''
          )
        )
      ].filter((item) => !!item);
    }
    if (labels.length > 0 && activeRadio === 'year') {
      labels.push(`${labels[0]} n-1`);
    }
    return labels;
  };

  // Calculate derived values for rendering
  const dataForm = form || {};
  const client_wines = data?.client_wines || [];
  const rival_wines = data?.rival_wines || [];
  let rivals = getRivals(client_wines, rival_wines, wine);
  let competitorNames = getCompetitorNames(rivals);
  const columns = getColumns();

  const handleExportOptions = {
    wines: JSON.stringify([get(wine, 'label')]),
    rivals: JSON.stringify(dataForm.competitors || [])
  };
  const dataTable = getDataTable();
  const currentCampaign = form?.currentCampaign;
  const dataChart = getDataChart();
  const totalDn = getTotalDn(dataChart);
  const labels = getLabels(dataChart);

  return (
    <React.Fragment>
      <Card id="distribution-millesime">
        <ContainerPills>
          <CardHeader
            title={I18n.t('restaurants&caves.vintage.titleGraph')}
            fontSize="20px"
            iconRight="right"
            rightComponents={
              <HeaderBar>
                <div className="headerInputsContainer ignored-export">
                  <ChartInputsBloc
                    wine={wine}
                    vintageEnd={vintageEnd}
                    vintageStart={vintageStart}
                    wineOptions={getWineOptions(competitorNames)}
                    vintageOptions={getVintageOptions()}
                    bigOptions
                    onChange={(field, value) => handleChangeChart(field, value)}
                  />
                </div>
                <HeaderRightComponents>
                  <ExportButton
                    name={`${get(form, 'currentCampaign.network.name')}-${get(
                      form,
                      'currentCampaign.market.name'
                    )}-${get(form, 'currentCampaign.year')}-${join(
                      map(get(form, 'wine'), 'label'),
                      '-'
                    )}-${I18n.t('restaurants&caves.vintage.titleGraph')}`}
                    path={`/distribution/${get(
                      currentCampaign,
                      'id'
                    )}/vintage/xls?no_page`}
                    options={handleExportOptions}
                    xls
                    jpg
                    pdf
                    targetElement="#distribution-millesime"
                    newApi
                  />
                </HeaderRightComponents>
              </HeaderBar>
            }
          />
        </ContainerPills>
        <BarGraph
          data={dataChart}
          height={400}
          renderTooltip={(props) => renderTooltip(props, labels, totalDn)}
          labels={labels}
          tooltipOffset={{ x: 60, y: 30 }}
          colors={['#e25d34', '#1f2b37']}
        />
        <ChartCompareBloc
          competitorName={competitorName}
          compare={[
            {
              value: 'none',
              label: I18n.t('restaurants&caves.vintage.noCompare')
            },
            {
              value: 'year',
              label: I18n.t('restaurants&caves.vintage.compareLastYear')
            },
            {
              value: 'rival',
              label: I18n.t('restaurants&caves.vintage.compareRival')
            }
          ].map((item) => ({ ...item, checked: item.value === activeRadio }))}
          competitorNames={
            competitorNames && competitorNames.length > 0
              ? competitorNames.map((item) => ({
                  value: item,
                  label: item
                }))
              : []
          }
          onChange={(field, value) => handleChangeChart(field, value)}
          handleRadioChange={(value) =>
            setState((prev) => ({ ...prev, activeRadio: value }))
          }
        />
      </Card>
      <div style={{ marginTop: 20 }}>
        <Card id="Distribution-indicateurs-millésimes">
          <CardHeader
            title={I18n.t('restaurants&caves.vintage.tableTitle')}
            fontSize="20px"
            rightComponents={
              <HeaderRightComponents className="ignored-export">
                <ExportButton
                  name={`${get(form, 'currentCampaign.network.name')}-${get(
                    form,
                    'currentCampaign.market.name'
                  )}-${get(form, 'currentCampaign.year')}-${join(
                    map(get(form, 'wine'), 'label'),
                    '-'
                  )}-prix-moyen-millesime`}
                  targetElement="#Distribution-indicateurs-millésimes"
                  path={`/distribution/${get(
                    currentCampaign,
                    'id'
                  )}/vintage/xls?no_page`}
                  options={handleExportOptions}
                  xls
                  jpg
                  pdf
                  newApi
                />
              </HeaderRightComponents>
            }
          />
          <Table
            columns={columns}
            data={dataTable}
            sorted={sorted}
            expanded={expanded}
          />
        </Card>
      </div>
      <Spinner loading={isLoading} />
    </React.Fragment>
  );
};

export default Vintages;
